import { createModel } from "xstate/lib/model";
import {
  IPaymentMethodType,
  payByLink,
  registerPayment,
} from "@nju/data-access-portal-payment";
import { IPaymentMethodFormType } from "@nju/data-access-portal-payment";
import { info } from "@nju/data-access-portal-user";
import { ContractError, isError } from "@nju/result";
import { generalErrorMessage } from "@nju/utils";

type ISubmit = (transactionLink?: string) => void;

const model = createModel(
  {
    orchestratorId: undefined as undefined | string,
    onSubmit: undefined as undefined | ISubmit,
    transactionLink: undefined as undefined | string,
    amount: undefined as undefined | number,
    formError: undefined as undefined | string,
    paymentMethod: undefined as undefined | IPaymentMethodFormType,
  },
  {
    events: {
      SET_PAYMENT_METHOD: (value: IPaymentMethodFormType) => {
        return { value };
      },
      PAY_BY_LINK: () => {
        return {};
      },
      PAY_LATER: () => {
        return {};
      },
      ADD_CREDIT_CARD: () => {
        return {};
      },
      ERROR: (formError?: string) => {
        return { formError };
      },
      PAY_BY_LINK_ERROR: () => {
        return {};
      },
      PAYMENT_METHOD_SET_SUCCESS: ({
        transactionLink,
      }: {
        transactionLink?: string;
      }) => {
        return { transactionLink };
      },
      PAY_BY_LINK_SUCCESS: ({
        transactionLink,
      }: {
        transactionLink?: string;
      }) => {
        return { transactionLink };
      },
    },
  },
);

const setError = model.assign((_, { formError }) => {
  return {
    formError,
  };
}, "ERROR");

const setPaymentMethodTransactionLink = model.assign(
  (_, { transactionLink }) => {
    return {
      transactionLink,
    };
  },
  "PAYMENT_METHOD_SET_SUCCESS",
);

const setPayByLinkTransactionLink = model.assign((_, { transactionLink }) => {
  return {
    transactionLink,
  };
}, "PAY_BY_LINK_SUCCESS");

const setPaymentMethod = model.assign((_, { value }) => {
  return {
    paymentMethod: value,
    formError: undefined,
  };
}, "SET_PAYMENT_METHOD");

const validate = model.assign(({ paymentMethod }) => {
  return {
    formError: paymentMethod
      ? undefined
      : "Wybierz sposób płatności, aby przejść dalej.",
  };
});

export const paymentFormMachine = model.createMachine(
  {
    initial: "init",
    states: {
      init: {
        on: {
          SET_PAYMENT_METHOD: {
            actions: setPaymentMethod,
          },
          PAY_BY_LINK: {
            target: "payByLink",
          },
          PAY_LATER: {
            target: "payLater",
          },
          ADD_CREDIT_CARD: {
            target: "addCreditCard",
          },
        },
      },
      addCreditCard: {
        entry: validate,
        always: {
          cond: "isFormError",
          target: "init",
        },
        invoke: {
          src: "paymentMethodSet",
        },
        on: {
          PAYMENT_METHOD_SET_SUCCESS: {
            actions: [
              setPaymentMethodTransactionLink,
              "pushDataLayer",
              "nextStep",
            ],
            target: "final",
          },
          ERROR: {
            actions: [setError, "redirectToDashboard"],
            target: "init",
          },
        },
      },
      payLater: {
        entry: validate,
        always: {
          cond: "isFormError",
          target: "init",
        },
        invoke: {
          src: "paymentMethodSet",
        },
        on: {
          PAYMENT_METHOD_SET_SUCCESS: {
            actions: [setPaymentMethodTransactionLink, "skipPayment"],
            target: "final",
          },
          ERROR: {
            actions: [setError, "redirectToDashboard"],
            target: "init",
          },
        },
      },
      payByLink: {
        entry: validate,
        always: {
          cond: "isFormError",
          target: "init",
        },
        invoke: {
          src: "paymentMethodSet",
        },
        on: {
          PAYMENT_METHOD_SET_SUCCESS: {
            target: "makePaymentByLink",
          },
          ERROR: {
            actions: [setError, "redirectToDashboard"],
            target: "init",
          },
        },
      },
      makePaymentByLink: {
        invoke: {
          src: "paymentByLink",
        },
        on: {
          PAY_BY_LINK_SUCCESS: {
            actions: [setPayByLinkTransactionLink, "nextStep"],
            target: "final",
          },
          PAY_BY_LINK_ERROR: {
            actions: "redirectTiPayByLinkErrorPage",
            target: "init",
          },
        },
      },
      final: {
        id: "final",
        type: "final",
      },
    },
  },
  {
    guards: {
      isFormError: ({ formError }) => {
        return formError !== undefined;
      },
    },
    actions: {
      nextStep: ({ onSubmit, transactionLink }) => {
        if (onSubmit) {
          onSubmit(transactionLink);
        }
      },
      skipPayment: ({ onSubmit }) => {
        if (onSubmit) {
          onSubmit();
        }
      },
    },
    services: {
      paymentMethodSet: ({ orchestratorId, paymentMethod }) => {
        return async (callback) => {
          if (!paymentMethod || !orchestratorId) {
            throw new ContractError({
              message: "Missing `paymentMethod` or `orchestratorId` value",
              extra: {
                where: "payment form",
              },
            });
          }

          const payload = {
            orchestratorId,
            paymentMethod:
              paymentMethod === "PAY_BY_LINK"
                ? "DIGITAL_WALLET"
                : (paymentMethod as IPaymentMethodType),
          };

          const result = await registerPayment(payload);

          if (!isError(result)) {
            const transactionLink =
              "transactionLink" in result.value.payload
                ? result.value.payload.transactionLink
                : undefined;

            callback({
              type: "PAYMENT_METHOD_SET_SUCCESS",
              transactionLink,
            });
          } else {
            callback({
              type: "ERROR",
              formError: generalErrorMessage,
            });
          }
        };
      },
      paymentByLink: ({ amount }) => {
        return async (callback) => {
          if (!amount) {
            throw new ContractError({
              message: "Missing `amount`",
            });
          }

          const infoResult = await info();

          if (isError(infoResult)) {
            return callback({
              type: "ERROR",
              formError: generalErrorMessage,
            });
          }

          const { id } = infoResult.value;

          const payByLinkResult = await payByLink({
            amount,
            userId: id,
          });

          if (isError(payByLinkResult)) {
            return callback({
              type: "PAY_BY_LINK_ERROR",
            });
          }

          if (payByLinkResult.value.status === "FAILED") {
            return callback({
              type: "PAY_BY_LINK_ERROR",
            });
          }

          const { transactionLink } = payByLinkResult.value;

          callback({
            type: "PAY_BY_LINK_SUCCESS",
            transactionLink,
          });
        };
      },
    },
  },
);
